import { useState } from 'react';
import type { Cliente } from '../../../api/ops/clientes';
import { Campo } from '../../../componentes/Campo';
import { Ventana } from '../../../componentes/Ventana';
import type { ContextoClientes } from '../lista/contexto';
import { textoDeError } from '../lista/textos';

/**
 * Marcar o quitar «Bloqueada por obligación legal» (G196) con su motivo, que queda en Auditoría: `placeCustomerLegalHold`
 * (motivo de 3 a 500 caracteres) y `releaseCustomerLegalHold` (de 10 a 500). Solo quien atiende la protección de datos
 * (`clientes.rgpd.atender`). Textos propuestos por la aplicación, a validar por la asesoría con la próxima exportación.
 */
export function VentanaBloqueo({ c, cliente, modo, nombreNegocio, onCerrar, onHecho }: { c: ContextoClientes; cliente: Cliente; modo: 'poner' | 'quitar' | null; nombreNegocio: string; onCerrar: () => void; onHecho: () => void }) {
    const [motivo, setMotivo] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const minimo = modo === 'quitar' ? 10 : 3;
    const error = intentado && motivo.trim().length < minimo ? (motivo.trim() === '' ? 'Escribe el motivo.' : `Escribe al menos ${String(minimo)} caracteres.`) : '';

    const cerrar = () => {
        setMotivo('');
        setIntentado(false);
        onCerrar();
    };

    const hacer = () => {
        setIntentado(true);
        if (modo === null || motivo.trim().length < minimo) {
            return;
        }
        setOcupado(true);
        const peticion = modo === 'poner' ? c.ops.placeCustomerLegalHold(c.negocio, cliente.id, motivo.trim()) : c.ops.releaseCustomerLegalHold(c.negocio, cliente.id, motivo.trim());
        peticion
            .then(() => {
                c.avisar({ tipo: 'ok', texto: modo === 'poner' ? 'Ficha bloqueada por obligación legal.' : 'Bloqueo quitado.' });
                setMotivo('');
                setIntentado(false);
                onHecho();
            })
            .catch((e: unknown) => {
                c.avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido hacerlo. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    return (
        <Ventana
            abierta={modo !== null}
            titulo={modo === 'quitar' ? 'Quitar el bloqueo' : 'Bloquear por obligación legal o reclamación'}
            texto={
                modo === 'quitar'
                    ? `Si ${cliente.display_name} pide borrar sus datos, su ficha en ${nombreNegocio} se anonimizará como cualquier otra.`
                    : `Mientras esté bloqueada, si ${cliente.display_name} pide borrar sus datos, su ficha no se anonimiza: queda sin uso y solo a disposición de jueces y autoridades. El motivo queda en Actividad.`
            }
            accion={modo === 'quitar' ? 'Quitar el bloqueo' : 'Bloquear'}
            accionIcono={modo === 'quitar' ? 'lock-open' : 'lock'}
            ocupado={ocupado}
            ocupadoTexto={modo === 'quitar' ? 'Quitando…' : 'Bloqueando…'}
            movil={c.movil}
            onAccion={hacer}
            onCerrar={cerrar}
        >
            <Campo etiqueta="Motivo" valor={motivo} onChange={setMotivo} error={error} ayuda="Por ejemplo, la reclamación o la obligación legal que lo pide." filas={2} max={500} nombre="motivo-bloqueo" />
        </Ventana>
    );
}
